import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  HardHat,
  Users,
  Receipt,
  AlertTriangle,
  Sparkles,
  ArrowRight,
  MapPin,
  Radio,
  CreditCard,
  FileText,
  FileSignature,
  Clock,
  Wallet,
  CalendarOff,
  Package,
  CheckCheck,
} from "lucide-react";
import {
  LiveSitesMap,
  type SiteMarker,
  type WorkerMarker,
} from "@/components/dashboard/LiveSitesMap";
import { ContractArchivesCard } from "@/components/dashboard/ContractArchivesCard";
import { ClockAlertsEmployeeCard } from "@/components/dashboard/ClockAlertsEmployeeCard";
import { ClockAlertsAdminCard } from "@/components/dashboard/ClockAlertsAdminCard";
import { InactiveEmployeesCard } from "@/components/dashboard/InactiveEmployeesCard";
import { useScopedCompanyId } from "@/owner/useScopedCompanyId";
import { useOwnerCompany } from "@/owner/OwnerCompanyContext";
import { useWeeklyAlerts } from "@/hooks/useWeeklyAlerts";
import { trackAdsConversion } from "@/lib/analytics";

export const Route = createFileRoute("/_app/dashboard")({
  component: Dashboard,
  head: () => ({
    meta: [{ title: "Dashboard — Business Dite Ads" }],
  }),
});

type ActiveEntry = {
  id: string;
  user_id: string;
  job_site_id: string | null;
  clock_in: string;
  clock_in_latitude: number | null;
  clock_in_longitude: number | null;
  clock_in_selfie_path: string | null;
};
type SiteRow = {
  id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  project_id: string;
  project_name?: string | null;
};
type ProfileLite = { id: string; full_name: string | null; email: string };

function formatMoney(currency: string, amount: number) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
}

function Dashboard() {
  const { t } = useTranslation();
  const { profile, company, hasRole } = useAuth();
  const isAdminOrSupervisor = hasRole("admin") || hasRole("supervisor");
  const { subscription } = useSubscription();

  const subscriptionPastDue = subscription?.status === "past_due";
  const subscriptionPaused = subscription?.status === "paused";
  const firstName = profile?.full_name?.split(" ")[0] ?? t("dashboard.unknown");
  const scopedCompanyId = useScopedCompanyId();
  const { isOwner, companies, loading: ownerCompaniesLoading } = useOwnerCompany();
  const companyId = company?.id;
  const currency = company?.currency ?? "USD";
  const weeklyAlerts = useWeeklyAlerts({ companyId: scopedCompanyId ?? companyId });
  const ownerCompanyIds = useMemo(() => companies.map((companyRow) => companyRow.id), [companies]);

  // Google Ads conversion tracking after successful Paddle checkout redirect
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") !== "success") return;
    const key = "gads_conv_fired";
    if (sessionStorage.getItem(key)) return;
    trackAdsConversion("AW-18151480671/ltZ0CIqFsKocEN-6ps9D", {
      value: 1.0,
      currency: "USD",
      transaction_id: "",
    });
    sessionStorage.setItem(key, "1");
  }, []);

  const [revenueMTD, setRevenueMTD] = useState(0);
  const [activeProjects, setActiveProjects] = useState(0);
  const [pendingInvoices, setPendingInvoices] = useState(0);
  const [overdueInvoices, setOverdueInvoices] = useState(0);
  const [invoicesDueSoon, setInvoicesDueSoon] = useState(0);
  const [estimatesAwaiting, setEstimatesAwaiting] = useState(0);
  const [estimatesExpiringSoon, setEstimatesExpiringSoon] = useState(0);
  const [estimatesDraft, setEstimatesDraft] = useState(0);
  const [contractsAwaitingSignature, setContractsAwaitingSignature] = useState(0);
  const [contractsDraft, setContractsDraft] = useState(0);
  const [invoicesDraft, setInvoicesDraft] = useState(0);
  const [timeEntriesPending, setTimeEntriesPending] = useState(0);
  const [expensesPending, setExpensesPending] = useState(0);
  const [absencesPending, setAbsencesPending] = useState(0);
  const [unpaidApprovedHours, setUnpaidApprovedHours] = useState(0);
  const [unpaidApprovedCount, setUnpaidApprovedCount] = useState(0);
  const [unpaidApprovedMaxDays, setUnpaidApprovedMaxDays] = useState(0);
  const [materialsLowStock, setMaterialsLowStock] = useState(0);
  const [projectsToInvoice, setProjectsToInvoice] = useState(0);
  const [clientsWithoutEstimate, setClientsWithoutEstimate] = useState(0);
  const [clientsWithoutContract, setClientsWithoutContract] = useState(0);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [active, setActive] = useState<ActiveEntry[]>([]);
  const [profilesById, setProfilesById] = useState<Record<string, ProfileLite>>({});
  const [loading, setLoading] = useState(true);

  // Initial load
  useEffect(() => {
    if ((!isOwner && !companyId) || (isOwner && ownerCompaniesLoading)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      const today = new Date();
      const todayIso = today.toISOString().slice(0, 10);
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoIso = sevenDaysAgo.toISOString();
      const sevenDaysAhead = new Date(today);
      sevenDaysAhead.setDate(sevenDaysAhead.getDate() + 7);
      const sevenDaysAheadIso = sevenDaysAhead.toISOString().slice(0, 10);

      const scopeCompanyIds = isOwner
        ? scopedCompanyId
          ? [scopedCompanyId]
          : ownerCompanyIds
        : companyId
          ? [companyId]
          : [];

      const applyScope = <
        T extends { eq: (col: string, val: string) => T; in: (col: string, values: string[]) => T },
      >(
        q: T,
      ): T => {
        if (scopeCompanyIds.length === 0) return q;
        if (scopeCompanyIds.length === 1) return q.eq("company_id", scopeCompanyIds[0]);
        return q.in("company_id", scopeCompanyIds);
      };

      const [
        paymentsRes,
        projectsRes,
        invoicesRes,
        overdueRes,
        dueSoonRes,
        estimatesAwaitingRes,
        estimatesExpiringRes,
        contractsAwaitingRes,
        estimatesDraftRes,
        contractsDraftRes,
        invoicesDraftRes,
        timeEntriesPendingRes,
        expensesPendingRes,
        absencesPendingRes,
        approvedTimeEntriesRes,
        payrollLinksRes,
        materialsRes,
        sitesRes,
        activeRes,
        profilesRes,
        activeProjectsListRes,
        invoicesThisMonthRes,
        clientsAllRes,
        estimatesClientIdsRes,
        contractsClientIdsRes,
      ] = await Promise.all([
        applyScope(
          supabase
            .from("invoice_payments")
            .select("amount")
            .gte("payment_date", monthStart.toISOString().slice(0, 10)) as any,
        ),
        applyScope(
          supabase
            .from("projects")
            .select("id", { count: "exact", head: true })
            .eq("status", "active") as any,
        ),
        applyScope(
          supabase
            .from("invoices")
            .select("id", { count: "exact", head: true })
            .in("status", ["sent", "partial", "overdue"]) as any,
        ),
        applyScope(
          supabase
            .from("invoices")
            .select("id", { count: "exact", head: true })
            .eq("status", "overdue") as any,
        ),
        applyScope(
          supabase
            .from("invoices")
            .select("id", { count: "exact", head: true })
            .in("status", ["sent", "partial"])
            .gte("due_date", todayIso)
            .lte("due_date", sevenDaysAheadIso) as any,
        ),
        applyScope(
          supabase
            .from("estimates")
            .select("id", { count: "exact", head: true })
            .eq("status", "sent") as any,
        ),
        applyScope(
          supabase
            .from("estimates")
            .select("id", { count: "exact", head: true })
            .eq("status", "sent")
            .gte("expiry_date", todayIso)
            .lte("expiry_date", sevenDaysAheadIso) as any,
        ),
        applyScope(
          supabase
            .from("contracts")
            .select("id", { count: "exact", head: true })
            .eq("status", "sent") as any,
        ),
        applyScope(
          supabase
            .from("estimates")
            .select("id", { count: "exact", head: true })
            .eq("status", "draft") as any,
        ),
        applyScope(
          supabase
            .from("contracts")
            .select("id", { count: "exact", head: true })
            .eq("status", "draft") as any,
        ),
        applyScope(
          supabase
            .from("invoices")
            .select("id", { count: "exact", head: true })
            .eq("status", "draft") as any,
        ),
        applyScope(
          supabase
            .from("time_entries")
            .select("id", { count: "exact", head: true })
            .eq("status", "pending")
            .not("clock_out", "is", null) as any,
        ),
        applyScope(
          supabase
            .from("expenses")
            .select("id", { count: "exact", head: true })
            .eq("status", "pending") as any,
        ),
        applyScope(
          supabase
            .from("absence_justifications")
            .select("id", { count: "exact", head: true })
            .eq("status", "pending") as any,
        ),
        applyScope(
          supabase
            .from("time_entries")
            .select("id, hours, approved_at")
            .eq("status", "approved")
            .not("clock_out", "is", null) as any,
        ),
        applyScope(supabase.from("payroll_run_entry_links").select("time_entry_id") as any),
        applyScope(
          supabase
            .from("materials")
            .select("id, stock_level, reorder_level")
            .eq("is_active", true) as any,
        ),
        applyScope(
          supabase
            .from("job_sites")
            .select("id, name, latitude, longitude, project_id, company_id")
            .eq("is_active", true)
            .eq("show_on_map", true) as any,
        ),
        applyScope(
          supabase
            .from("time_entries")
            .select(
              "id, user_id, job_site_id, clock_in, clock_in_latitude, clock_in_longitude, clock_in_selfie_path",
            )
            .is("clock_out", null) as any,
        ),
        applyScope(supabase.from("profiles").select("id, full_name, email") as any),
        applyScope(
          supabase
            .from("projects")
            .select("id, client_id, contract_id")
            .eq("status", "active") as any,
        ),
        applyScope(
          supabase
            .from("invoices")
            .select("client_id, contract_id")
            .gte("issue_date", monthStart.toISOString().slice(0, 10))
            .neq("status", "cancelled") as any,
        ),
        applyScope(supabase.from("clients").select("id, is_active").eq("is_active", true) as any),
        applyScope(
          supabase.from("estimates").select("client_id").not("client_id", "is", null) as any,
        ),
        applyScope(
          supabase.from("contracts").select("client_id").not("client_id", "is", null) as any,
        ),
      ]);

      if (cancelled) return;

      const revenue = (paymentsRes.data ?? []).reduce(
        (sum: number, p: { amount: number | null }) => sum + Number(p.amount ?? 0),
        0,
      );
      setRevenueMTD(revenue);
      setActiveProjects(projectsRes.count ?? 0);
      setPendingInvoices(invoicesRes.count ?? 0);
      setOverdueInvoices(overdueRes.count ?? 0);
      setInvoicesDueSoon(dueSoonRes.count ?? 0);
      setEstimatesAwaiting(estimatesAwaitingRes.count ?? 0);
      setEstimatesExpiringSoon(estimatesExpiringRes.count ?? 0);
      setContractsAwaitingSignature(contractsAwaitingRes.count ?? 0);
      setEstimatesDraft(estimatesDraftRes.count ?? 0);
      setContractsDraft(contractsDraftRes.count ?? 0);
      setInvoicesDraft(invoicesDraftRes.count ?? 0);
      setTimeEntriesPending(timeEntriesPendingRes.count ?? 0);
      setExpensesPending(expensesPendingRes.count ?? 0);
      setAbsencesPending(absencesPendingRes.count ?? 0);

      // Approved time entries that have not been paid (no payroll link)
      const linkedEntryIds = new Set(
        ((payrollLinksRes.data ?? []) as Array<{ time_entry_id: string }>).map(
          (l) => l.time_entry_id,
        ),
      );
      const approvedEntries = (
        (approvedTimeEntriesRes.data ?? []) as Array<{
          id: string;
          hours: number | null;
          approved_at: string | null;
        }>
      ).filter((e) => !linkedEntryIds.has(e.id));
      const nowMs = Date.now();
      let maxDays = 0;
      let totalHours = 0;
      approvedEntries.forEach((e) => {
        totalHours += Number(e.hours ?? 0);
        if (e.approved_at) {
          const days = Math.floor((nowMs - new Date(e.approved_at).getTime()) / 86400000);
          if (days > maxDays) maxDays = days;
        }
      });
      setUnpaidApprovedCount(approvedEntries.length);
      setUnpaidApprovedHours(totalHours);
      setUnpaidApprovedMaxDays(maxDays);

      const lowStock = (
        (materialsRes.data ?? []) as Array<{
          stock_level: number | null;
          reorder_level: number | null;
        }>
      ).filter(
        (m) =>
          Number(m.reorder_level ?? 0) > 0 &&
          Number(m.stock_level ?? 0) <= Number(m.reorder_level ?? 0),
      ).length;
      setMaterialsLowStock(lowStock);

      // Active projects without an invoice issued this month.
      // Match by contract_id when present, otherwise fall back to client_id.
      const monthInvoices = (invoicesThisMonthRes.data ?? []) as Array<{
        client_id: string | null;
        contract_id: string | null;
      }>;
      const invoicedContractIds = new Set(
        monthInvoices.map((i) => i.contract_id).filter(Boolean) as string[],
      );
      const invoicedClientIds = new Set(
        monthInvoices.map((i) => i.client_id).filter(Boolean) as string[],
      );
      const activeProjectsList = (activeProjectsListRes.data ?? []) as Array<{
        id: string;
        client_id: string | null;
        contract_id: string | null;
      }>;
      const projectsMissingInvoice = activeProjectsList.filter((p) => {
        if (p.contract_id && invoicedContractIds.has(p.contract_id)) return false;
        if (p.client_id && invoicedClientIds.has(p.client_id)) return false;
        return true;
      }).length;
      setProjectsToInvoice(projectsMissingInvoice);

      // Active clients without any estimate / without any contract
      const allClients = (clientsAllRes.data ?? []) as Array<{ id: string }>;
      const clientsWithEstimateSet = new Set(
        ((estimatesClientIdsRes.data ?? []) as Array<{ client_id: string | null }>)
          .map((e) => e.client_id)
          .filter(Boolean) as string[],
      );
      const clientsWithContractSet = new Set(
        ((contractsClientIdsRes.data ?? []) as Array<{ client_id: string | null }>)
          .map((c) => c.client_id)
          .filter(Boolean) as string[],
      );
      setClientsWithoutEstimate(allClients.filter((c) => !clientsWithEstimateSet.has(c.id)).length);
      setClientsWithoutContract(allClients.filter((c) => !clientsWithContractSet.has(c.id)).length);

      const rawSites = (sitesRes.data ?? []) as Array<SiteRow & { company_id?: string }>;
      const projectIds = Array.from(
        new Set(rawSites.map((site) => site.project_id).filter(Boolean)),
      );
      let projectNameById = new Map<string, string>();
      let projectsErrorMsg: string | null = null;
      let projectsCount = 0;
      if (projectIds.length > 0) {
        const projectsBaseQuery = supabase.from("projects").select("id, name").in("id", projectIds);
        const projectsQuery = applyScope(projectsBaseQuery as any);
        const { data: projectRows, error: projErr } = await projectsQuery;
        projectNameById = new Map(
          (projectRows ?? []).map((project: { id: string; name: string }) => [
            project.id,
            project.name,
          ]),
        );
        projectsCount = projectRows?.length ?? 0;
        projectsErrorMsg = projErr?.message ?? null;
      }

      setSites(
        rawSites.map((site) => ({
          ...site,
          project_name: projectNameById.get(site.project_id) ?? null,
        })),
      );
      setActive((activeRes.data ?? []) as ActiveEntry[]);
      const map: Record<string, ProfileLite> = {};
      (profilesRes.data ?? []).forEach((p: ProfileLite) => {
        map[p.id] = p;
      });
      setProfilesById(map);

      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, scopedCompanyId, isOwner, ownerCompanyIds, ownerCompaniesLoading]);

  // Refresh helper: pulls the latest active (not clocked-out) entries.
  // Used by realtime, polling, and visibility/focus handlers as a safety net,
  // because a missed realtime event would otherwise leave the live map stale
  // (e.g. selfie clock-ins from a tokenized link in another tab).
  useEffect(() => {
    if ((!isOwner && !companyId) || (isOwner && ownerCompaniesLoading)) return;
    let cancelled = false;

    const refreshActive = async () => {
      const scopeCompanyIds = isOwner
        ? scopedCompanyId
          ? [scopedCompanyId]
          : ownerCompanyIds
        : companyId
          ? [companyId]
          : [];

      let q = supabase
        .from("time_entries")
        .select(
          "id, user_id, job_site_id, clock_in, clock_in_latitude, clock_in_longitude, clock_in_selfie_path",
        )
        .is("clock_out", null);
      if (scopeCompanyIds.length === 1) {
        q = q.eq("company_id", scopeCompanyIds[0]);
      } else if (scopeCompanyIds.length > 1) {
        q = q.in("company_id", scopeCompanyIds);
      }
      const { data } = await q;
      if (!cancelled) setActive((data ?? []) as ActiveEntry[]);
    };

    // Realtime channel only when scoped to a single company (filter requires a value)
    const channel = scopedCompanyId
      ? supabase
          .channel(`dashboard-time-entries-${scopedCompanyId}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "time_entries",
              filter: `company_id=eq.${scopedCompanyId}`,
            },
            () => {
              void refreshActive();
            },
          )
          .subscribe()
      : null;

    // Polling fallback every 20s in case a realtime event is missed
    const interval = window.setInterval(() => {
      void refreshActive();
    }, 20_000);

    // Refresh when the tab regains focus / becomes visible
    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshActive();
    };
    const onFocus = () => {
      void refreshActive();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [companyId, scopedCompanyId, isOwner, ownerCompanyIds, ownerCompaniesLoading]);

  const totalClockedIn = active.length;

  const siteMarkers = useMemo<SiteMarker[]>(() => {
    const counts = new Map<string, number>();
    active.forEach((e) => {
      if (!e.job_site_id) return;
      counts.set(e.job_site_id, (counts.get(e.job_site_id) ?? 0) + 1);
    });
    return sites
      .filter((s) => s.latitude != null && s.longitude != null)
      .map((s) => ({
        id: s.id,
        name: s.name,
        latitude: Number(s.latitude),
        longitude: Number(s.longitude),
        activeCount: counts.get(s.id) ?? 0,
        projectName: s.project_name ?? null,
      }));
  }, [sites, active]);

  // Per-worker markers using GPS captured from selfie/clock-in.
  // Useful when the job site has no coordinates, or to show real worker
  // position separate from the site pin.
  const workerMarkers = useMemo<WorkerMarker[]>(() => {
    const sitesById = new Map(sites.map((s) => [s.id, s]));
    return active
      .map((e) => {
        const p = profilesById[e.user_id];
        const site = e.job_site_id ? sitesById.get(e.job_site_id) : null;
        // Show every active worker on the site badge, even if the clock-in
        // did not capture GPS. Fallback to the job site coordinates so the
        // supervisor can see all people on a location when hovering.
        const hasGps = e.clock_in_latitude != null && e.clock_in_longitude != null;
        const lat = e.clock_in_latitude ?? site?.latitude;
        const lng = e.clock_in_longitude ?? site?.longitude;
        if (lat == null || lng == null) return null;
        return {
          id: e.id,
          name: p?.full_name || p?.email || t("dashboard.unknown"),
          latitude: Number(lat),
          longitude: Number(lng),
          siteName: site?.name ?? null,
          clockInAt: e.clock_in,
          selfiePath: e.clock_in_selfie_path,
          gpsLatitude: hasGps ? Number(e.clock_in_latitude) : null,
          gpsLongitude: hasGps ? Number(e.clock_in_longitude) : null,
        };
      })
      .filter(Boolean) as WorkerMarker[];
  }, [active, sites, profilesById, t]);

  const sitesWithActive = useMemo(() => {
    const counts = new Map<string, ActiveEntry[]>();
    active.forEach((e) => {
      if (!e.job_site_id) return;
      const arr = counts.get(e.job_site_id) ?? [];
      arr.push(e);
      counts.set(e.job_site_id, arr);
    });
    return sites
      .map((s) => ({ site: s, entries: counts.get(s.id) ?? [] }))
      .filter((x) => x.entries.length > 0)
      .sort((a, b) => b.entries.length - a.entries.length);
  }, [sites, active]);

  const unassignedActive = active.filter((e) => !e.job_site_id);
  const sitesWithoutCoords = sites.filter((s) => s.latitude == null || s.longitude == null).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {t("dashboard.welcome", { name: firstName })}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("dashboard.subtitle", { company: company?.name ?? t("dashboard.yourCompany") })}
        </p>
      </div>

      <ClockAlertsEmployeeCard />
      {isAdminOrSupervisor && <ClockAlertsAdminCard />}
      {isAdminOrSupervisor && <InactiveEmployeesCard />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t("dashboard.stats.revenueMTD")}
          value={formatMoney(currency, revenueMTD)}
          delta={loading ? t("common.loading") : t("dashboard.stats.revenueDelta")}
          icon={TrendingUp}
        />
        <StatCard
          label={t("dashboard.stats.activeProjects")}
          value={String(activeProjects)}
          delta={
            loading
              ? t("common.loading")
              : activeProjects === 0
                ? t("dashboard.stats.noActiveProjects")
                : t("dashboard.stats.inProgress")
          }
          icon={HardHat}
        />
        <StatCard
          label={t("dashboard.stats.clockedIn")}
          value={String(totalClockedIn)}
          delta={
            loading
              ? t("common.loading")
              : totalClockedIn === 0
                ? t("dashboard.stats.nobodyOnClock")
                : t("dashboard.stats.live")
          }
          icon={Users}
          live
        />
        <StatCard
          label={t("dashboard.stats.invoicesPending")}
          value={String(pendingInvoices)}
          delta={
            loading
              ? t("common.loading")
              : pendingInvoices === 0
                ? t("dashboard.stats.allClear")
                : t("dashboard.stats.sentPartialOverdue")
          }
          icon={Receipt}
        />
      </div>

      {/* Live map + roster */}
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b p-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold">{t("dashboard.liveSites")}</h2>
            <Badge variant="secondary" className="gap-1">
              <Radio className="h-3 w-3 animate-pulse text-emerald-500" />
              {t("dashboard.realtime")}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("dashboard.activeSitesSummary", {
              active: totalClockedIn,
              sites: siteMarkers.length,
            })}
            {sitesWithoutCoords > 0
              ? ` · ${t("dashboard.withoutCoords", { count: sitesWithoutCoords })}`
              : ""}
          </p>
        </div>

        <div className="grid gap-0 lg:grid-cols-[1.6fr_1fr]">
          <div className="p-4">
            {siteMarkers.length === 0 && workerMarkers.length === 0 ? (
              <div className="flex h-[360px] flex-col items-center justify-center rounded-lg border border-dashed text-center">
                <MapPin className="mb-2 h-6 w-6 text-muted-foreground" />
                <p className="text-sm font-medium">{t("dashboard.noMappedSites")}</p>
                <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                  {t("dashboard.noMappedSitesDesc")}
                </p>
                <Button asChild size="sm" variant="outline" className="mt-3">
                  <Link to="/job-sites">{t("dashboard.manageJobSites")}</Link>
                </Button>
              </div>
            ) : (
              <LiveSitesMap
                sites={siteMarkers}
                workers={workerMarkers}
                radiusMeters={Number(company?.gps_default_radius_meters) || 100}
              />
            )}
          </div>

          <div className="border-t p-4 lg:border-l lg:border-t-0">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("dashboard.activeBySite")}
            </h3>
            {sitesWithActive.length === 0 && unassignedActive.length === 0 ? (
              <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                {t("dashboard.nobodyClockedIn")}
              </div>
            ) : (
              <ul className="max-h-[320px] space-y-3 overflow-y-auto pr-1">
                {sitesWithActive.map(({ site, entries }) => (
                  <li key={site.id} className="rounded-md border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <Link
                        to="/job-sites/$jobSiteId"
                        params={{ jobSiteId: site.id }}
                        className="text-sm font-medium hover:underline"
                      >
                        {site.name}
                      </Link>
                      <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
                        {entries.length}
                      </Badge>
                    </div>
                    {site.project_name && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{site.project_name}</p>
                    )}
                    {/* Todos os nomes, em ordem alfabética. Ordena pelo nome JÁ resolvido
                        (full_name → email → "desconhecido") para não misturar critérios, e
                        com localeCompare para respeitar acentos e ignorar maiúsculas. */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {entries
                        .map((e) => {
                          const p = profilesById[e.user_id];
                          return {
                            id: e.id,
                            name: p?.full_name || p?.email || t("dashboard.unknown"),
                          };
                        })
                        .sort((a, b) =>
                          a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
                        )
                        .map((e) => (
                          <Badge key={e.id} variant="secondary" className="font-normal">
                            {e.name}
                          </Badge>
                        ))}
                    </div>
                  </li>
                ))}
                {unassignedActive.length > 0 && (
                  <li className="rounded-md border border-dashed p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        {t("dashboard.withoutJobSite")}
                      </span>
                      <Badge variant="outline">{unassignedActive.length}</Badge>
                    </div>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </Card>

      {/* Empty state CTA — only if truly empty */}
      {activeProjects === 0 && totalClockedIn === 0 && pendingInvoices === 0 && (
        <Card className="overflow-hidden border-dashed p-0">
          <div className="grid gap-0 md:grid-cols-2">
            <div className="p-6 sm:p-8">
              <div
                className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg text-primary-foreground"
                style={{ background: "var(--gradient-brand)" }}
              >
                <Sparkles className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-semibold">{t("dashboard.getStarted")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.getStartedDesc")}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/clients">
                    {t("dashboard.addClient")}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/estimates">{t("dashboard.createEstimate")}</Link>
                </Button>
              </div>
            </div>
            <div
              className="hidden md:block"
              style={{ background: "var(--gradient-cool)" }}
              aria-hidden
            />
          </div>
        </Card>
      )}

      <ContractArchivesCard />

      <Card className="p-6">
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-accent" />
          <h3 className="text-sm font-semibold">{t("dashboard.alerts")}</h3>
        </div>
        {(() => {
          const hasEstimateAlerts =
            estimatesAwaiting > 0 || estimatesExpiringSoon > 0 || estimatesDraft > 0;
          const hasContractAlerts = contractsAwaitingSignature > 0 || contractsDraft > 0;
          const hasClientGapAlerts = clientsWithoutEstimate > 0 || clientsWithoutContract > 0;
          const showInvoiceAlerts = weeklyAlerts.shouldShow("invoices_due");
          const hasInvoiceAlerts =
            showInvoiceAlerts &&
            (overdueInvoices > 0 ||
              invoicesDueSoon > 0 ||
              pendingInvoices > 0 ||
              invoicesDraft > 0);
          const showTimeAlerts = weeklyAlerts.shouldShow("time_tracking_pending");
          const hasTimeAlerts = showTimeAlerts && (timeEntriesPending > 0 || absencesPending > 0);
          const hasExpenseAlerts = expensesPending > 0;
          const showPayrollAlerts = weeklyAlerts.shouldShow("payroll_pending");
          const hasPayrollAlerts = showPayrollAlerts && unpaidApprovedCount > 0;
          const showInventoryAlerts = weeklyAlerts.shouldShow("low_stock_materials");
          const hasInventoryAlerts = showInventoryAlerts && materialsLowStock > 0;
          const showProjectsToInvoiceAlerts = weeklyAlerts.shouldShow("projects_to_invoice");
          const hasProjectsToInvoiceAlerts = showProjectsToInvoiceAlerts && projectsToInvoice > 0;
          const hasOtherAlerts =
            sitesWithoutCoords > 0 || subscriptionPastDue || subscriptionPaused;
          const hasAnyAlert =
            hasEstimateAlerts ||
            hasContractAlerts ||
            hasClientGapAlerts ||
            hasInvoiceAlerts ||
            hasTimeAlerts ||
            hasExpenseAlerts ||
            hasPayrollAlerts ||
            hasInventoryAlerts ||
            hasProjectsToInvoiceAlerts ||
            hasOtherAlerts;
          if (!hasAnyAlert) {
            return <p className="text-sm text-muted-foreground">{t("dashboard.noAlerts")}</p>;
          }
          return (
            <div className="space-y-4">
              {(subscriptionPastDue || subscriptionPaused) && (
                <ul className="space-y-2 text-sm">
                  {subscriptionPastDue && (
                    <li className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                      <span className="flex items-start gap-2 text-destructive">
                        <CreditCard className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{t("dashboard.subscriptionPastDue")}</span>
                      </span>
                      <Button asChild size="sm" variant="destructive">
                        <Link to="/upgrade">{t("dashboard.fixBilling")}</Link>
                      </Button>
                    </li>
                  )}
                  {subscriptionPaused && (
                    <li className="flex items-center justify-between gap-3 rounded-md border border-accent/40 bg-accent/10 p-3">
                      <span className="flex items-start gap-2">
                        <CreditCard className="mt-0.5 h-4 w-4 shrink-0 text-accent-foreground" />
                        <span>{t("dashboard.subscriptionPaused")}</span>
                      </span>
                      <Button asChild size="sm">
                        <Link to="/upgrade">{t("dashboard.resume")}</Link>
                      </Button>
                    </li>
                  )}
                </ul>
              )}

              {hasEstimateAlerts && (
                <AlertGroup
                  icon={<FileText className="h-3.5 w-3.5" />}
                  title={t("dashboard.alertGroupEstimates")}
                >
                  {estimatesAwaiting > 0 && (
                    <AlertRow
                      severity="warning"
                      label={t("dashboard.estimatesAwaitingResponse", { count: estimatesAwaiting })}
                      to="/estimates"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                  {estimatesExpiringSoon > 0 && (
                    <AlertRow
                      severity="warning"
                      label={t("dashboard.estimatesExpiringSoon", { count: estimatesExpiringSoon })}
                      to="/estimates"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                  {estimatesDraft > 0 && (
                    <AlertRow
                      severity="info"
                      label={t("dashboard.estimatesDraftPending", { count: estimatesDraft })}
                      to="/estimates"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                </AlertGroup>
              )}

              {hasContractAlerts && (
                <AlertGroup
                  icon={<FileSignature className="h-3.5 w-3.5" />}
                  title={t("dashboard.alertGroupContracts")}
                >
                  {contractsAwaitingSignature > 0 && (
                    <AlertRow
                      severity="warning"
                      label={t("dashboard.contractsAwaitingSignature", {
                        count: contractsAwaitingSignature,
                      })}
                      to="/contracts"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                  {contractsDraft > 0 && (
                    <AlertRow
                      severity="info"
                      label={t("dashboard.contractsDraftPending", { count: contractsDraft })}
                      to="/contracts"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                </AlertGroup>
              )}

              {hasClientGapAlerts && (
                <AlertGroup
                  icon={<Users className="h-3.5 w-3.5" />}
                  title={t("dashboard.alertGroupClients")}
                >
                  {clientsWithoutEstimate > 0 && (
                    <AlertRow
                      severity="info"
                      label={t("dashboard.clientsWithoutEstimate", {
                        count: clientsWithoutEstimate,
                      })}
                      to="/clients"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                  {clientsWithoutContract > 0 && (
                    <AlertRow
                      severity="info"
                      label={t("dashboard.clientsWithoutContract", {
                        count: clientsWithoutContract,
                      })}
                      to="/clients"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                </AlertGroup>
              )}

              {hasInvoiceAlerts && (
                <AlertGroup
                  icon={<Receipt className="h-3.5 w-3.5" />}
                  title={t("dashboard.alertGroupInvoices")}
                >
                  {overdueInvoices > 0 && (
                    <AlertRow
                      severity="danger"
                      label={t("dashboard.invoicesOverdue", { count: overdueInvoices })}
                      to="/invoices"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                  {invoicesDueSoon > 0 && (
                    <AlertRow
                      severity="warning"
                      icon={<Clock className="h-3.5 w-3.5" />}
                      label={t("dashboard.invoicesDueSoon", { count: invoicesDueSoon })}
                      to="/invoices"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                  {pendingInvoices > 0 && (
                    <AlertRow
                      severity="info"
                      label={t("dashboard.invoicesAwaiting", { count: pendingInvoices })}
                      to="/invoices"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                  {invoicesDraft > 0 && (
                    <AlertRow
                      severity="info"
                      label={t("dashboard.invoicesDraftPending", { count: invoicesDraft })}
                      to="/invoices"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                </AlertGroup>
              )}

              {hasTimeAlerts && (
                <AlertGroup
                  icon={<Clock className="h-3.5 w-3.5" />}
                  title={t("dashboard.alertGroupTimeTracking")}
                >
                  {timeEntriesPending > 0 && (
                    <AlertRow
                      severity="warning"
                      label={t("dashboard.timeEntriesPending", { count: timeEntriesPending })}
                      to="/time-tracking"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                  {absencesPending > 0 && (
                    <AlertRow
                      severity="warning"
                      icon={<CalendarOff className="h-3.5 w-3.5" />}
                      label={t("dashboard.absencesPending", { count: absencesPending })}
                      to="/time-tracking"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                </AlertGroup>
              )}

              {hasExpenseAlerts && (
                <AlertGroup
                  icon={<Wallet className="h-3.5 w-3.5" />}
                  title={t("dashboard.alertGroupExpenses")}
                >
                  <AlertRow
                    severity="warning"
                    label={t("dashboard.expensesPending", { count: expensesPending })}
                    to="/expenses"
                    actionLabel={t("dashboard.review")}
                  />
                </AlertGroup>
              )}

              {hasPayrollAlerts && (
                <AlertGroup
                  icon={<CheckCheck className="h-3.5 w-3.5" />}
                  title={t("dashboard.alertGroupPayroll")}
                >
                  {unpaidApprovedCount > 0 && (
                    <AlertRow
                      severity={unpaidApprovedMaxDays >= 7 ? "danger" : "warning"}
                      icon={<Clock className="h-3.5 w-3.5" />}
                      label={
                        unpaidApprovedMaxDays > 0
                          ? t("dashboard.unpaidApprovedHoursWithDelay", {
                              count: unpaidApprovedCount,
                              hours: unpaidApprovedHours.toFixed(1),
                              days: unpaidApprovedMaxDays,
                            })
                          : t("dashboard.unpaidApprovedHours", {
                              count: unpaidApprovedCount,
                              hours: unpaidApprovedHours.toFixed(1),
                            })
                      }
                      to="/payroll"
                      actionLabel={t("dashboard.review")}
                    />
                  )}
                </AlertGroup>
              )}

              {hasInventoryAlerts && (
                <AlertGroup
                  icon={<Package className="h-3.5 w-3.5" />}
                  title={t("dashboard.alertGroupInventory")}
                >
                  <AlertRow
                    severity="warning"
                    label={t("dashboard.materialsLowStock", { count: materialsLowStock })}
                    to="/materials"
                    actionLabel={t("dashboard.review")}
                  />
                </AlertGroup>
              )}

              {hasProjectsToInvoiceAlerts && (
                <AlertGroup
                  icon={<HardHat className="h-3.5 w-3.5" />}
                  title={t("dashboard.alertGroupProjectsToInvoice")}
                >
                  <AlertRow
                    severity="warning"
                    icon={<Receipt className="h-3.5 w-3.5" />}
                    label={t("dashboard.projectsToInvoice", { count: projectsToInvoice })}
                    to="/invoices/new"
                    actionLabel={t("dashboard.review")}
                  />
                </AlertGroup>
              )}

              {sitesWithoutCoords > 0 && (
                <AlertGroup
                  icon={<MapPin className="h-3.5 w-3.5" />}
                  title={t("dashboard.alertGroupOther")}
                >
                  <AlertRow
                    severity="info"
                    label={t("dashboard.sitesMissingGps", { count: sitesWithoutCoords })}
                    to="/job-sites"
                    actionLabel={t("dashboard.fix")}
                  />
                </AlertGroup>
              )}
            </div>
          );
        })()}
      </Card>
    </div>
  );
}

function StatCard({
  label,
  value,
  delta,
  icon: Icon,
  live,
}: {
  label: string;
  value: string;
  delta: string;
  icon: typeof TrendingUp;
  live?: boolean;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          <p className="mt-2 flex items-center gap-2 text-2xl font-bold tracking-tight">
            {value}
            {live && (
              <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            )}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{delta}</p>
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
}

function AlertGroup({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {icon}
        <span>{title}</span>
      </div>
      <ul className="space-y-1.5 text-sm">{children}</ul>
    </div>
  );
}

function AlertRow({
  severity,
  label,
  to,
  actionLabel,
  icon,
}: {
  severity: "danger" | "warning" | "info";
  label: string;
  to: string;
  actionLabel: string;
  icon?: React.ReactNode;
}) {
  const styles =
    severity === "danger"
      ? "border-destructive/30 bg-destructive/5"
      : severity === "warning"
        ? "border-accent/40 bg-accent/10"
        : "border-border bg-muted/30";
  const dotClass =
    severity === "danger"
      ? "bg-destructive"
      : severity === "warning"
        ? "bg-accent"
        : "bg-muted-foreground/40";
  return (
    <li className={`flex items-center justify-between gap-3 rounded-md border p-2.5 ${styles}`}>
      <span className="flex items-center gap-2">
        {icon ? (
          <span className="text-muted-foreground">{icon}</span>
        ) : (
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotClass}`} aria-hidden />
        )}
        <span>{label}</span>
      </span>
      <Button asChild size="sm" variant="ghost">
        <Link to={to}>{actionLabel}</Link>
      </Button>
    </li>
  );
}
